/* eslint-disable */
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { GripVertical, Save, Check, Package } from 'lucide-react';

const ALL_VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
const DEPTS = ['kitchen', 'bar'];

const C = {
  yellow: '#F7EC71', yellowLight: '#FEFCE8', yellowDark: '#E4AC49',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  green: '#7EA594', greenLight: '#EAF2EF',
  cream: '#F7F6EF', charcoal: '#2F3A42',
  navy: '#445366', mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  white: '#FFFFFF', red: '#DC3545', redLight: '#FFF0F0',
  pink: '#F2B8CB', pinkLight: '#FDF0F5', pinkText: '#C4607A',
  amber: '#E07B00', amberLight: '#FFF3E0',
};

const pill = (active) => ({
  padding: '0.35rem 1rem', borderRadius: '999px',
  border: `1.5px solid ${active ? C.navy : C.border}`,
  background: active ? C.navy : C.white,
  color: active ? C.white : C.mid,
  fontSize: '0.83rem', cursor: 'pointer',
  fontWeight: active ? 700 : 500, transition: 'all 0.15s',
  whiteSpace: 'nowrap',
});

const smallPill = (active, color) => ({
  padding: '0.28rem 0.85rem', borderRadius: '999px',
  border: `1.5px solid ${active ? (color || C.navy) : C.border}`,
  background: active ? (color || C.navy) : C.white,
  color: active ? C.white : C.mid,
  fontSize: '0.8rem', cursor: 'pointer',
  fontWeight: active ? 700 : 500, transition: 'all 0.15s',
  whiteSpace: 'nowrap',
});

export default function ProductOrder({ products, currentUser }) {
  const [suppliers, setSuppliers] = useState([]);
  const [contextType, setContextType] = useState('supplier'); // 'supplier' | 'stocktake'
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedDept, setSelectedDept] = useState('kitchen');
  const [sortOrders, setSortOrders] = useState({}); // { productId: position }
  const [orderedList, setOrderedList] = useState([]); // products in display order
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  // Drag state
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [dragging, setDragging] = useState(null);

  // Load suppliers on mount
  useEffect(() => {
    supabase.from('suppliers').select('id, name').eq('active', true).order('name')
      .then(({ data }) => { if (data) setSuppliers(data); });
  }, []);

  // Derive context_id from current selection
  function getContextId() {
    if (contextType === 'supplier') return selectedSupplier?.name || null;
    if (contextType === 'stocktake') {
      if (!selectedVenue || !selectedDept) return null;
      return `${selectedVenue}__${selectedDept}`;
    }
    return null;
  }

  // Filter products relevant to current context
  function getRelevantProducts() {
    const contextId = getContextId();
    if (!contextId) return [];

    if (contextType === 'supplier') {
      return products.filter(p => {
        if (!p.active) return false;
        const ft = p.fulfilmentType || p.fulfilment_type || 'external';
        if (ft !== 'external' && ft !== 'both') return false;
        const sid = p.supplierId || p.supplier_id;
        return sid === selectedSupplier?.id;
      });
    }

    if (contextType === 'stocktake') {
      return products.filter(p => {
        if (!p.active) return false;
        if (!(p.stockTracked || p.stock_tracked)) return false;
        const availableAt = p.availableAt || p.available_at || [];
        if (!availableAt.includes(selectedVenue)) return false;
        if ((p.dept || 'kitchen') !== selectedDept) return false;
        const ft = p.fulfilmentType || p.fulfilment_type || 'external';
        const df = p.defaultFulfiller || p.default_fulfiller || '';
        // Internal tab products for this venue+dept
        return ft === 'internal' || (ft === 'both' && df !== selectedVenue);
      });
    }
    return [];
  }

  // Load saved sort orders whenever context changes
  useEffect(() => {
    const contextId = getContextId();
    if (!contextId) { setOrderedList([]); setSortOrders({}); return; }

    setLoading(true);
    supabase.from('product_sort_orders')
      .select('product_id, position')
      .eq('context_type', contextType)
      .eq('context_id', contextId)
      .then(({ data }) => {
        const map = {};
        if (data) data.forEach(row => { map[row.product_id] = row.position; });
        setSortOrders(map);

        const relevant = getRelevantProducts();
        // Split into positioned and unpositioned
        const positioned = relevant
          .filter(p => map[p.id] !== undefined)
          .sort((a, b) => map[a.id] - map[b.id]);
        const unpositioned = relevant
          .filter(p => map[p.id] === undefined)
          .sort((a, b) => a.name.localeCompare(b.name));
        setOrderedList([...positioned, ...unpositioned]);
        setLoading(false);
      });
  }, [contextType, selectedSupplier, selectedVenue, selectedDept]);

  // ── Drag handlers ─────────────────────────────────────────────────
  function handleDragStart(e, index) {
    dragItem.current = index;
    setDragging(index);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragEnter(e, index) {
    dragOverItem.current = index;
    e.preventDefault();
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e) {
    e.preventDefault();
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) { resetDrag(); return; }

    const newList = [...orderedList];
    const draggedProduct = newList[dragItem.current];
    newList.splice(dragItem.current, 1);
    newList.splice(dragOverItem.current, 0, draggedProduct);
    setOrderedList(newList);
    resetDrag();
  }

  function handleDragEnd() { resetDrag(); }

  function resetDrag() {
    dragItem.current = null;
    dragOverItem.current = null;
    setDragging(null);
  }

  // ── Save positions ────────────────────────────────────────────────
  async function handleSave() {
    const contextId = getContextId();
    if (!contextId || orderedList.length === 0) return;
    setSaving(true);

    const upserts = orderedList.map((p, idx) => ({
      product_id:   p.id,
      context_type: contextType,
      context_id:   contextId,
      position:     idx,
    }));

    const { error } = await supabase
      .from('product_sort_orders')
      .upsert(upserts, { onConflict: 'product_id,context_type,context_id' });

    setSaving(false);
    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      // Update local sortOrders map
      const newMap = {};
      orderedList.forEach((p, idx) => { newMap[p.id] = idx; });
      setSortOrders(newMap);
    }
  }

  const contextId = getContextId();
  const hasContext = !!contextId;
  const hasPositioned = orderedList.some(p => sortOrders[p.id] !== undefined);

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.5rem', fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>
          Product Order
        </h2>
        <p style={{ margin: 0, fontSize: '0.85rem', color: C.mid }}>
          Set the display order of products per supplier or stocktake context
        </p>
      </div>

      {/* Context type toggle */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
          Order type
        </p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => { setContextType('supplier'); setSelectedSupplier(null); }} style={pill(contextType === 'supplier')}>
            🚚 External — by supplier
          </button>
          <button onClick={() => { setContextType('stocktake'); setSelectedVenue(null); }} style={pill(contextType === 'stocktake')}>
            📋 Internal — stocktake order
          </button>
        </div>
      </div>

      {/* Context selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        {contextType === 'supplier' && (
          <>
            <p style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
              Supplier
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {suppliers.map(s => (
                <button key={s.id} onClick={() => setSelectedSupplier(s)}
                  style={smallPill(selectedSupplier?.id === s.id, C.skyText)}>
                  {s.name}
                </button>
              ))}
            </div>
            {suppliers.length === 0 && (
              <p style={{ color: C.mid, fontSize: '0.85rem' }}>No active suppliers found.</p>
            )}
          </>
        )}

        {contextType === 'stocktake' && (
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
                Venue
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {ALL_VENUES.map(v => (
                  <button key={v} onClick={() => setSelectedVenue(v)}
                    style={smallPill(selectedVenue === v)}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p style={{ fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
                Dept
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {DEPTS.map(d => (
                  <button key={d} onClick={() => setSelectedDept(d)}
                    style={smallPill(selectedDept === d, d === 'bar' ? C.pinkText : C.skyText)}>
                    {d === 'bar' ? '🍹 Bar' : '🍳 Kitchen'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main panel */}
      {!hasContext ? (
        <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '3rem', textAlign: 'center', color: C.mid }}>
          <Package size={28} style={{ color: C.border, marginBottom: '0.75rem' }} />
          <p style={{ fontWeight: 600, color: C.navy, marginBottom: '0.25rem', margin: '0 0 0.25rem' }}>
            Select a context to start ordering
          </p>
          <p style={{ fontSize: '0.83rem', margin: 0 }}>
            {contextType === 'supplier'
              ? 'Pick a supplier above to see their products'
              : 'Pick a venue and dept above to set stocktake order'}
          </p>
        </div>
      ) : loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: C.mid }}>Loading products…</div>
      ) : orderedList.length === 0 ? (
        <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '3rem', textAlign: 'center', color: C.mid }}>
          <Package size={28} style={{ color: C.border, marginBottom: '0.75rem' }} />
          <p style={{ fontWeight: 600, color: C.navy, margin: '0 0 0.25rem' }}>No products found</p>
          <p style={{ fontSize: '0.83rem', margin: 0 }}>
            {contextType === 'supplier'
              ? 'No active products are assigned to this supplier'
              : 'No tracked products found for this venue and dept'}
          </p>
        </div>
      ) : (
        <>
          {/* Instructions + save */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <p style={{ margin: '0 0 0.2rem', fontSize: '0.88rem', fontWeight: 600, color: C.charcoal }}>
                {contextType === 'supplier'
                  ? `${selectedSupplier?.name} — ${orderedList.length} product${orderedList.length !== 1 ? 's' : ''}`
                  : `${selectedVenue} · ${selectedDept} — ${orderedList.length} product${orderedList.length !== 1 ? 's' : ''}`}
              </p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: C.mid }}>
                Drag <GripVertical size={11} style={{ verticalAlign: 'middle' }} /> to reorder — matches the order on the {contextType === 'supplier' ? "supplier's website" : 'stocktake walk-around'}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {saved && (
                <span style={{ fontSize: '0.85rem', color: C.green, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Check size={14} /> Saved
                </span>
              )}
              <button onClick={handleSave} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1.25rem', background: saving ? C.border : C.navy, color: saving ? C.mid : C.white, border: 'none', borderRadius: '8px', cursor: saving ? 'default' : 'pointer', fontWeight: 700, fontSize: '0.88rem', transition: 'all 0.15s' }}>
                <Save size={14} />
                {saving ? 'Saving…' : 'Save Order'}
              </button>
            </div>
          </div>

          {/* Drag list */}
          <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '32px 32px 1fr auto auto', gap: '0', padding: '0.5rem 1rem', background: C.cream, borderBottom: `1px solid ${C.border}` }}>
              <div />
              <div style={{ fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>#</div>
              <div style={{ fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Product</div>
              <div style={{ fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '1rem' }}>Unit</div>
              <div style={{ fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</div>
            </div>

            {orderedList.map((product, index) => {
              const isPositioned = sortOrders[product.id] !== undefined;
              const isDraggingThis = dragging === index;
              const isBar = (product.dept || '') === 'bar';
              const deptColor = isBar ? C.pinkText : C.skyText;
              const deptBg = isBar ? C.pinkLight : C.skyBlueLight;

              return (
                <div
                  key={product.id}
                  draggable
                  onDragStart={e => handleDragStart(e, index)}
                  onDragEnter={e => handleDragEnter(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 32px 1fr auto auto',
                    alignItems: 'center',
                    gap: '0',
                    padding: '0.7rem 1rem',
                    borderBottom: index < orderedList.length - 1 ? `1px solid ${C.border}` : 'none',
                    background: isDraggingThis ? C.yellowLight : !isPositioned ? `${C.cream}88` : C.white,
                    cursor: 'grab',
                    transition: 'background 0.12s',
                    opacity: isDraggingThis ? 0.5 : 1,
                    userSelect: 'none',
                  }}
                >
                  {/* Drag handle */}
                  <div style={{ color: C.border, display: 'flex', alignItems: 'center', cursor: 'grab' }}>
                    <GripVertical size={16} />
                  </div>

                  {/* Position number */}
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: isPositioned ? C.navy : C.border, minWidth: '20px' }}>
                    {index + 1}
                  </div>

                  {/* Name + dept badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                    <span style={{ background: deptBg, color: deptColor, borderRadius: '4px', padding: '0.08rem 0.42rem', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', flexShrink: 0 }}>
                      {isBar ? 'Bar' : 'Kitchen'}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: C.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {product.name}
                    </span>
                    {!isPositioned && (
                      <span style={{ fontSize: '0.68rem', color: C.mid, fontWeight: 500, flexShrink: 0 }}>unsorted</span>
                    )}
                  </div>

                  {/* Unit */}
                  <div style={{ marginRight: '1rem' }}>
                    <span style={{ background: C.skyBlueLight, color: C.skyText, borderRadius: '4px', padding: '0.1rem 0.45rem', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {product.unit}
                    </span>
                  </div>

                  {/* Category */}
                  <div style={{ fontSize: '0.8rem', color: C.mid, whiteSpace: 'nowrap' }}>
                    {product.category || '—'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Unsorted hint */}
          {!hasPositioned && (
            <p style={{ fontSize: '0.78rem', color: C.mid, marginTop: '0.75rem', textAlign: 'center' }}>
              Drag products into order then hit Save — currently showing alphabetical
            </p>
          )}
        </>
      )}
    </div>
  );
}